import React, { createContext, PropsWithChildren, useContext, useMemo, useState } from 'react'
import im, { Draft } from 'immer'
import capitalize from 'lodash/capitalize'
import {
  CustomFieldDefinition,
  CustomFieldUsage,
  CustomValues,
  DocumentStatus,
  FieldListConfig,
  FileType,
  Tenant,
} from '@alucio/aws-beacon-amplify/src/models'
import { DocumentORM } from 'src/types/types'
import {
  AI_GENERATED_REPORTING_NAME_PREFIX,
  aiGeneratedLabelDocumentFilter,
  defaultTenantFieldDocumentFilter,
  FilterEntry,
  filters,
  getAiGeneratedLabelFieldId,
  getAiGeneratedLabelValueId,
  isAiGeneratedLabelFilter,
  merge,
  modalFilterSystemFieldsQueries,
} from 'src/state/redux/document/query'
import { useCurrentUserORM, useUserTenant } from 'src/state/redux/selector/user'
import {
  FilterAndSortOptions,
  selectAiGeneratedLabelFilterEntries,
  selectLatestDocumentVersionAiGeneratedLabelFilterEntries,
} from 'src/state/redux/selector/document'
import { useTenantCustomFields } from 'src/state/redux/selector/tenant';
import { useLocation } from 'src/router'
import ROUTES from 'src/router/routeDef'
import useIsDebugView from 'src/hooks/useIsDebugView/useIsDebugView'
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags'
import { useSelector } from 'src/state/redux'

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface DocumentCount {
  [fieldId: string]: {
    [valueId: string]: number
  }
}

export type SetFilterStateFn = React.Dispatch<React.SetStateAction<FilterEntry[]>>

export interface FilterCategoryConfig {
  fieldId: string,
  fieldName: string,
  configKey: string,
  isAiGenerated?: boolean,
}

export interface DNADocumentFilterContextValue {
  activeFilters: FilterEntry[],
  setActiveFilters: SetFilterStateFn,
  filterEntries: FilterEntry[],
  filterCategoryOrder: FilterCategoryConfig[],
  restrictToConfiguredFilterCategories: boolean,
  filterSelectorOpts: FilterAndSortOptions<DocumentORM>,
  toggleFilter: (filterName: string, filterValue: string, c?: SetFilterStateFn) => void,
  resetToDefaultFilters: (c?: SetFilterStateFn) => void,
  clearFilters: (c?: SetFilterStateFn) => void,
  unpublishedToggle: boolean,
  toggleUnpublished: () => void,
  applyDefaultFiltersToInProgress: boolean,
}

export interface FilterMap {
  [fieldId: string]: {
    fieldName: string,
    values: {
      [valueId: string]: {
        fieldName: string,
        fieldId: string,
        valueId: string,
        fieldValue: string,
        active: boolean,
        isAiGeneratedLabel?: boolean,
      }
    }
  }
}

const isAiGeneratedDocumentLabelField = (field: CustomFieldDefinition): boolean =>
  field.reportingName?.startsWith(AI_GENERATED_REPORTING_NAME_PREFIX) ?? false;

const STATUS_DISPLAY_TO_ENUM: Record<string, DocumentStatus> = {
  'Revoked': DocumentStatus.REVOKED,
  'Published': DocumentStatus.PUBLISHED,
  'Archived': DocumentStatus.ARCHIVED,
  'Not Published': DocumentStatus.NOT_PUBLISHED,
};

const documentMatchesAiGeneratedLabelFilters = (
  documentORM: DocumentORM,
  fieldId: string,
  valueIds: Set<string>,
  useLatestDocumentVersion = false,
): boolean =>
  (useLatestDocumentVersion
    ? documentORM.relations.version.latestDocumentVersionORM.meta.ai.labels
    : documentORM.meta.ai.labels
  ).some((label) => (
    getAiGeneratedLabelFieldId(label.categoryKey) === fieldId &&
    valueIds.has(getAiGeneratedLabelValueId(label.categoryKey, label.value, label.normalizedValue))
  ));

const countAiGeneratedLabel = (documentCount: DocumentCount, fieldId: string, valueId: string) => {
  if (!documentCount[fieldId]) {
    documentCount[fieldId] = {};
  }

  if (!documentCount[fieldId][valueId]) {
    documentCount[fieldId][valueId] = 0;
  }

  documentCount[fieldId][valueId]++;
};

export const buildDocumentTenantFieldFilter = (
  activeFilters: FilterEntry[],
  unpublishedToggle: boolean,
): FilterAndSortOptions<DocumentORM> => {
  const activeCustomLabelFilters = activeFilters.filter(entry => entry.isCustomLabel);
  const activeAiLabelFilters = activeCustomLabelFilters.filter(isAiGeneratedLabelFilter);
  const activeTenantLabelFilters = activeCustomLabelFilters.filter(entry => !isAiGeneratedLabelFilter(entry));
  const requiredFilterFieldNames = Object.keys(
    activeTenantLabelFilters.reduce(
      (acc, activeFilter) => ({
        ...acc,
        [activeFilter.fieldId]: true,
      }),
      {},
    ))

  const tenantFieldFilter = defaultTenantFieldDocumentFilter(requiredFilterFieldNames, activeTenantLabelFilters)
  const aiLabelFilter = aiGeneratedLabelDocumentFilter(activeAiLabelFilters)

  if (unpublishedToggle) {
    return {
      filter: {
        relations: {
          version: (version) => (
            tenantFieldFilter(version.latestDocumentVersionORM.meta.customValues) &&
            aiLabelFilter(version.latestDocumentVersionORM.meta.ai)
          ),
        },
      },
    }
  }

  return {
    filter: {
      meta: {
        customValues: tenantFieldFilter,
        ai: aiLabelFilter,
      },
    },
  }
};

const SYSTEM_FILTER_CONFIGS: Record<string, FilterCategoryConfig> = {
  ASSOCIATED_FILES: {
    fieldId: 'Associated Files',
    fieldName: 'Associated Files',
    configKey: 'ASSOCIATED_FILES',
  },
  CONTENT_SOURCE: {
    fieldId: 'Content Source',
    fieldName: 'Content Source',
    configKey: 'CONTENT_SOURCE',
  },
  FILE_TYPE: {
    fieldId: 'Type',
    fieldName: 'Type',
    configKey: 'FILE_TYPE',
  },
  STATUS: {
    fieldId: 'Status',
    fieldName: 'Status',
    configKey: 'STATUS',
  },
};

const SYSTEM_FILTER_CATEGORY_IDS = new Set(
  Object.values(SYSTEM_FILTER_CONFIGS).map((config) => config.fieldId),
);

const getActiveListConfig = (tenant: Tenant | undefined, isPublisher: boolean) => {
  return isPublisher
    ? tenant?.config?.publisherListConfig
    : tenant?.config?.mslListConfig;
};

const mapFilterCategoryConfig = (
  fieldId: string,
  entries: FilterEntry[],
): FilterCategoryConfig | undefined => {
  const systemConfig = Object.values(SYSTEM_FILTER_CONFIGS).find((config) => config.fieldId === fieldId);
  if (systemConfig) {
    return systemConfig;
  }

  const customEntry = entries.find((entry) => entry.fieldId === fieldId);
  if (!customEntry) {
    return undefined;
  }

  return {
    fieldId,
    fieldName: customEntry.fieldName,
    configKey: fieldId,
    isAiGenerated: customEntry.isAiGeneratedLabel,
  };
};

const resolveConfiguredFilterCategory = (
  configuredField: FieldListConfig,
  availableEntries: FilterEntry[],
): FilterCategoryConfig | undefined => {
  const systemConfig = SYSTEM_FILTER_CONFIGS[configuredField.field];
  if (systemConfig) {
    return availableEntries.some((entry) => entry.fieldId === systemConfig.fieldId)
      ? systemConfig
      : undefined;
  }

  const customEntry = availableEntries.find((entry) => entry.fieldId === configuredField.field);
  if (!customEntry) {
    return undefined;
  }

  return {
    fieldId: customEntry.fieldId,
    fieldName: customEntry.fieldName,
    configKey: customEntry.fieldId,
    isAiGenerated: customEntry.isAiGeneratedLabel,
  };
};

export const resolveFilterCategoryOrder = (
  filterEntries: FilterEntry[],
  listConfig?: { filterDisplayFields?: FieldListConfig[] | null },
): {
  categories: FilterCategoryConfig[],
  restrictToConfiguredCategories: boolean,
} => {
  const availableFieldIds = Array.from(new Set(filterEntries.map((entry) => entry.fieldId)));
  const defaultCategoryOrder = availableFieldIds
    .map((fieldId) => mapFilterCategoryConfig(fieldId, filterEntries))
    .filter((config): config is FilterCategoryConfig => !!config);
  const aiGeneratedCategoryOrder = defaultCategoryOrder.filter((config) => config.isAiGenerated);

  const configuredFields = listConfig?.filterDisplayFields ?? [];
  if (!configuredFields.length) {
    return {
      categories: defaultCategoryOrder,
      restrictToConfiguredCategories: false,
    };
  }

  const seenFieldIds = new Set<string>();
  const configuredCategoryOrder = configuredFields
    .map((field) => resolveConfiguredFilterCategory(field, filterEntries))
    .filter((config): config is FilterCategoryConfig => !!config)
    .filter((config) => {
      if (seenFieldIds.has(config.fieldId)) {
        return false;
      }

      seenFieldIds.add(config.fieldId);
      return true;
    });
  const configuredFieldIds = new Set(configuredCategoryOrder.map((config) => config.fieldId));
  const missingAiGeneratedCategories = aiGeneratedCategoryOrder.filter(
    (config) => !configuredFieldIds.has(config.fieldId),
  );

  if (!configuredCategoryOrder.length) {
    return {
      categories: defaultCategoryOrder,
      restrictToConfiguredCategories: false,
    };
  }

  return {
    categories: [...configuredCategoryOrder, ...missingAiGeneratedCategories],
    restrictToConfiguredCategories: true,
  };
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Counts documents by custom values for filter display
 * Supports cross-category filtering: counts are calculated based on filters from OTHER categories
 */
export function countDocumentCustomValues(
  documents: DocumentORM[],
  isPublisher?: boolean,
  userTenant?: Tenant,
  activeFilters?: FilterEntry[],
  excludeFieldId?: string,
  useLatestDocumentVersion = false,
): DocumentCount {
  const documentCount: DocumentCount = {
    'Associated Files': {
      'Has associated files': 0,
    },
    'Content Source': {},
    Status: {
      'Revoked': 0,
      'Published': 0,
      'Archived': 0,
      'Not Published': 0,
    },
    Type: {},
  };

  let filteredDocuments = documents;
  if (activeFilters && excludeFieldId) {
    const crossCategoryFilters = activeFilters.filter(f => f.fieldId !== excludeFieldId);

    if (crossCategoryFilters.length > 0) {
      filteredDocuments = documents.filter((documentORM) => {
        const docVersionORM = useLatestDocumentVersion
          ? documentORM.relations.version.latestDocumentVersionORM
          : documentORM.relations.version.latestUsableDocumentVersionORM;

        const filtersByField: Record<string, Set<string>> = {};
        crossCategoryFilters.forEach(filter => {
          if (!filtersByField[filter.fieldId]) {
            filtersByField[filter.fieldId] = new Set();
          }
          filtersByField[filter.fieldId].add(filter.valueId);
        });

        return Object.entries(filtersByField).every(([fieldId, valueIds]) => {
          if (fieldId === 'Type') {
            return valueIds.has(documentORM.model.type);
          }

          if (fieldId === 'Status') {
            return Array.from(valueIds).some(valueId =>
              STATUS_DISPLAY_TO_ENUM[valueId] === documentORM.model.status,
            );
          }

          if (fieldId === 'Associated Files') {
            return valueIds.has('Has associated files') &&
                   docVersionORM?.relations.associatedFiles.length > 0;
          }

          if (fieldId === 'Content Source') {
            const integrationType = capitalize(docVersionORM?.model.integrationType || '');
            return valueIds.has(integrationType);
          }

          if (fieldId.startsWith(AI_GENERATED_REPORTING_NAME_PREFIX)) {
            return documentMatchesAiGeneratedLabelFilters(
              documentORM,
              fieldId,
              valueIds,
              useLatestDocumentVersion,
            );
          }

          const { configsMap } = useLatestDocumentVersion
            ? documentORM.relations.version.latestDocumentVersionORM.meta.customValues
            : documentORM.meta.customValues;
          if (configsMap && configsMap[fieldId]) {
            const fieldValues = configsMap[fieldId].valuesDefinition || [];
            return fieldValues.some(def => valueIds.has(def.id));
          }

          return false;
        });
      });
    }
  }

  for (const documentORM of filteredDocuments) {
    const docVersionORM = useLatestDocumentVersion
      ? documentORM.relations.version.latestDocumentVersionORM
      : documentORM.relations.version.latestUsableDocumentVersionORM

    // Count associated files
    if (docVersionORM?.relations.associatedFiles.length) {
      documentCount['Associated Files']['Has associated files']++;
    }

    // Count status (publisher only)
    if (isPublisher) {
      switch (documentORM.model.status) {
        case DocumentStatus.NOT_PUBLISHED:
          documentCount.Status['Not Published']++;
          break;
        case DocumentStatus.PUBLISHED:
          documentCount.Status.Published++;
          break;
        case DocumentStatus.ARCHIVED:
          documentCount.Status.Archived++;
          break;
        case DocumentStatus.REVOKED:
          documentCount.Status.Revoked++;
      }
    }

    // Count file types
    if (!documentCount.Type[documentORM.model.type]) {
      documentCount.Type[documentORM.model.type] = 0;
    }
    documentCount.Type[documentORM.model.type]++;

    // Count custom tenant fields
    const { configsMap } = useLatestDocumentVersion
      ? documentORM.relations.version.latestDocumentVersionORM.meta.customValues
      : documentORM.meta.customValues;
    if (configsMap) {
      Object.values(configsMap).forEach((value) => {
        if (!documentCount[value.field.id]) {
          documentCount[value.field.id] = {};
        }

        value.valuesDefinition?.forEach((definition) => {
          if (!documentCount[value.field.id][definition.id]) {
            documentCount[value.field.id][definition.id] = 0;
          }
          documentCount[value.field.id][definition.id]++;
        })
      })
    }

    const aiLabels = useLatestDocumentVersion
      ? documentORM.relations.version.latestDocumentVersionORM.meta.ai.labels
      : documentORM.meta.ai.labels;

    aiLabels.forEach((label) => {
      if (!label.categoryKey || !label.value) {
        return;
      }

      countAiGeneratedLabel(
        documentCount,
        getAiGeneratedLabelFieldId(label.categoryKey),
        getAiGeneratedLabelValueId(label.categoryKey, label.value, label.normalizedValue),
      );
    });

    // Count content source
    if (userTenant?.integrations?.length && documentORM.relations.version.latestUsableDocumentVersionORM) {
      const docVer = documentORM.relations.version.latestUsableDocumentVersionORM.model
      if (docVer.integrationType) {
        const integrationType = capitalize(docVer.integrationType)
        if (!documentCount['Content Source'][integrationType]) {
          documentCount['Content Source'][integrationType] = 0
        }
        documentCount['Content Source'][integrationType]++
      }
    }
  }

  return documentCount
}

// ============================================================================
// CONSTANTS
// ============================================================================

const NON_TENANT_SPECIFIC_FILTER_ENTRIES: FilterEntry[] = [
  {
    fieldId: 'Associated Files',
    fieldName: 'Associated Files',
    fieldValue: 'Has associated files',
    valueId: 'Has associated files',
    default: false,
    active: false,
  },
];

const STATUS_FILTER_ENTRIES: FilterEntry[] = [
  {
    fieldId: 'Status',
    fieldName: 'Status',
    fieldValue: 'Revoked',
    valueId: 'Revoked',
    default: false,
    active: false,
  },
  {
    fieldId: 'Status',
    fieldName: 'Status',
    fieldValue: 'Published',
    valueId: 'Published',
    default: true,
    active: true,
  },
  {
    fieldId: 'Status',
    fieldName: 'Status',
    fieldValue: 'Not Published',
    valueId: 'Not Published',
    default: false,
    active: false,
  },
  {
    fieldId: 'Status',
    fieldName: 'Status',
    fieldValue: 'Archived',
    valueId: 'Archived',
    default: false,
    active: false,
  },
];

const FORMAT_TYPE_FILTER_ENTRIES: FilterEntry[] = [
  {
    fieldId: 'Type',
    fieldName: 'Type',
    fieldValue: FileType.PPTX,
    valueId: FileType.PPTX,
    default: false,
    active: false,
  },
  {
    fieldId: 'Type',
    fieldName: 'Type',
    fieldValue: FileType.PDF,
    valueId: FileType.PDF,
    default: false,
    active: false,
  },
  {
    fieldId: 'Type',
    fieldName: 'Type',
    fieldValue: FileType.WEB,
    valueId: FileType.WEB,
    default: false,
    active: false,
  },
  {
    fieldId: 'Type',
    fieldName: 'Type',
    fieldValue: 'VIDEO',
    valueId: 'VIDEO',
    default: false,
    active: false,
  },
  {
    fieldId: 'Type',
    fieldName: 'Type',
    fieldValue: FileType.HTML,
    valueId: FileType.HTML,
    default: false,
    active: false,
  },
]

export function mapFieldFilterEntries(
  customFields: CustomFieldDefinition[],
  defaultFilters?: CustomValues[],
  userTenant?: Tenant,
  isPublisher?: boolean,
  skipStatus: boolean = false,
): FilterEntry[] {
  const defaultFieldsFilter: { [key: string]: { [key: string]: boolean } } = {}
  for (const field of defaultFilters || []) {
    defaultFieldsFilter[field.fieldId] = {}
    for (const fieldValue of field?.values ?? []) {
      defaultFieldsFilter[field.fieldId][fieldValue] = true
    }
  }

  const filterEntries: FilterEntry[] = []
  Object.values(FORMAT_TYPE_FILTER_ENTRIES).forEach((filterEntry: FilterEntry) => {
    filterEntries.unshift(filterEntry);
  });

  userTenant?.integrations?.forEach((integration: string | null) => {
    filterEntries.unshift({
      fieldId: 'Content Source',
      fieldName: 'Content Source',
      active: false,
      valueId: capitalize(integration!),
      fieldValue: capitalize(integration!),
      default: false,
    });
  });

  if (isPublisher && skipStatus === false) {
    Object.values(STATUS_FILTER_ENTRIES).forEach((filterEntry: FilterEntry) => {
      filterEntries.unshift(filterEntry);
    });
  }

  NON_TENANT_SPECIFIC_FILTER_ENTRIES.forEach((customFilterEntry: FilterEntry) => {
    filterEntries.unshift(customFilterEntry);
  });

  for (const field of customFields ?? []) {
    const isAiGeneratedLabel = isAiGeneratedDocumentLabelField(field);
    if (isAiGeneratedLabel) {
      continue;
    }

    for (const fieldValue of field.fieldValueDefinitions) {
      filterEntries.push({
        fieldId: field.id,
        fieldName: field.fieldLabel,
        fieldValue: fieldValue.label ?? fieldValue.value,
        valueId: fieldValue.id,
        default: defaultFieldsFilter[field.id]?.[fieldValue.id],
        active: defaultFieldsFilter[field.id]?.[fieldValue.id],
        isCustomLabel: true,
        isAiGeneratedLabel,
      })
    }
  }

  return filterEntries
}

// ============================================================================
// CONTEXT
// ============================================================================

export const DNADocumentFilterContext = createContext<DNADocumentFilterContextValue>({
  activeFilters: [],
  filterEntries: [],
  filterCategoryOrder: [],
  restrictToConfiguredFilterCategories: false,
  filterSelectorOpts: {},
  setActiveFilters: (p) => p,
  toggleFilter: () => { },
  resetToDefaultFilters: () => { },
  clearFilters: () => { },
  unpublishedToggle: false,
  toggleUnpublished: () => { },
  applyDefaultFiltersToInProgress: false,
})

DNADocumentFilterContext.displayName = 'DNADocumentFilterContext'

export const useDNADocumentFilters = () => useContext(DNADocumentFilterContext)

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export const DNADocumentFiltersProvider: React.FC<PropsWithChildren<{
  initUserDefaultFilters?: boolean,
  applyDefaultFiltersToInProgress?: boolean,
}>> = (props) => {
  const {
    children,
    initUserDefaultFilters = true,
    applyDefaultFiltersToInProgress = false,
  } = props
  const currentUser = useCurrentUserORM()
  const userTenant = useUserTenant()

  // TODO: we need to figure out a better way to validate if the current view is the publisher view
  const { pathname } = useLocation();
  const lowerCasedPathName = pathname.toLowerCase();
  const isPublisherSearch = lowerCasedPathName === ROUTES.SEARCH_RESULTS_PUBLISHER.PATH
  const isPublisher = [ROUTES.PUBLISHER_DASHBOARD.PATH].includes(lowerCasedPathName) || isPublisherSearch
  const isDebugRoute = useIsDebugView()
  const enableAiDocumentLabels = useFeatureFlags('BEAC_8172_ai_document_labels');
  const customFields = useTenantCustomFields({
    defaultSearchFilter: false,
    usages: { internalUsages: [CustomFieldUsage.DOCUMENT] },
  });
  const [unpublishedToggle, setUnpublishedToggle] = useState<boolean>(false)
  const selectedAiGeneratedFilterEntries = useSelector(
    unpublishedToggle
      ? selectLatestDocumentVersionAiGeneratedLabelFilterEntries
      : selectAiGeneratedLabelFilterEntries,
  );
  const aiGeneratedFilterEntries = enableAiDocumentLabels ? selectedAiGeneratedFilterEntries : [];
  const visibleCustomFields = useMemo(() => {
    return customFields.filter((field) => !isAiGeneratedDocumentLabelField(field));
  }, [customFields]);

  const filterEntries = useMemo(() => [
    ...mapFieldFilterEntries(
      visibleCustomFields,
      currentUser?.model?.defaultFiltersCustomValues,
      userTenant,
      isPublisher,
      unpublishedToggle,
    ),
    ...aiGeneratedFilterEntries,
  ], [currentUser, visibleCustomFields, userTenant, isPublisher, unpublishedToggle, aiGeneratedFilterEntries])

  const { filterCategoryOrder, restrictToConfiguredFilterCategories } = useMemo(() => {
    const configuredFilterEntries = filterEntries.filter((entry) => {
      if (entry.fieldId === SYSTEM_FILTER_CONFIGS.STATUS.fieldId) {
        return isPublisher;
      }

      if (SYSTEM_FILTER_CATEGORY_IDS.has(entry.fieldId)) {
        return true;
      }

      return entry.isCustomLabel;
    });

    const activeListConfig = getActiveListConfig(userTenant, isPublisher);

    const resolvedFilterCategories = resolveFilterCategoryOrder(configuredFilterEntries, activeListConfig);
    const existingCategoryIds = new Set(resolvedFilterCategories.categories.map(category => category.fieldId));
    const emptyAiGeneratedCategories = visibleCustomFields
      .filter(isAiGeneratedDocumentLabelField)
      .filter(field => field.fieldValueDefinitions.length > 0)
      .filter(field => !existingCategoryIds.has(field.id))
      .map<FilterCategoryConfig>(field => ({
        fieldId: field.id,
        fieldName: field.fieldLabel,
        configKey: field.id,
        isAiGenerated: true,
      }));

    return {
      filterCategoryOrder: [...resolvedFilterCategories.categories, ...emptyAiGeneratedCategories],
      restrictToConfiguredFilterCategories: resolvedFilterCategories.restrictToConfiguredCategories,
    };
  }, [filterEntries, isPublisher, userTenant, visibleCustomFields])

  const [activeFilters, setActiveFilters] = useState<FilterEntry[]>(
    initUserDefaultFilters
      ? filterEntries.filter(entry => entry.default)
      : [],
  )

  // Curried functions allow us to pass in any setState fn that has a FilterEntry[] type
  //  This allows us to be able to reuse these functions in the filter modal
  //  where actual filters changes are not committed until the Apply button is pressed
  function toggleFilter(
    fieldId: string,
    valueId: string,
    curriedFn: SetFilterStateFn = setActiveFilters,
  ) {
    const curried = im((draft: Draft<FilterEntry[]>) => {
      const activeFilterIdx =
        draft.findIndex(entry => entry.valueId === valueId && entry.fieldId === fieldId)
      if (activeFilterIdx < 0) {
        const targetFilter =
          filterEntries.find(entry => entry.valueId === valueId && entry.fieldId === fieldId)
        if (targetFilter) { draft.push({ ...targetFilter, active: true }) }
      } else {
        draft.splice(activeFilterIdx, 1)
      }
    })

    curriedFn(curried)
  }

  function resetToDefaultFilters(curriedFn: SetFilterStateFn = setActiveFilters) {
    const curried = (p: FilterEntry[]): typeof p => {
      return filterEntries.filter(entry => entry.default)
    }
    curriedFn(curried)
  }

  function clearFilters(curriedFn: SetFilterStateFn = setActiveFilters) {
    curriedFn(() => [])
  }

  // [TODO-2126] - I believe filters are still calculating at the document level, not the version level
  //             - Also, how do we want to handle counting in offline?
  const docTenantFieldFilter = useMemo(() => {
    return buildDocumentTenantFieldFilter(activeFilters, unpublishedToggle)
  }, [activeFilters, unpublishedToggle]);

  const docSystemFieldFilter = useMemo(() => {
    const activeFiltersMap = activeFilters.reduce<Record<string, string[]>>(
      (acc, activeFilter) => {
        if (!acc[activeFilter.fieldName])
        { acc[activeFilter.fieldName] = [activeFilter.fieldValue] }
        else
        { acc[activeFilter.fieldName].push(activeFilter.fieldValue) }

        return acc
      },
      { },
    )

    const activeSystemFieldFilters = Object
      .entries(modalFilterSystemFieldsQueries)
      .reduce<FilterAndSortOptions<DocumentORM>[]>(
        (acc, [systemFieldName, systemFieldQuery]) => {
          if (activeFiltersMap[systemFieldName])
          { acc.push(systemFieldQuery(activeFiltersMap[systemFieldName])) }
          return acc
        }
        , [],
      )

    const wrapperFnArr: FilterAndSortOptions<DocumentORM>[] = []
    activeSystemFieldFilters.forEach(filter => {
      if (filter.filter?.relations?.version) {
        wrapperFnArr.push(filter)
      }
    })

    const result = merge(...activeSystemFieldFilters)

    // We consolidate all filters that use version relations.version prop
    // Otherwise we will end up with only one filter for version prop after previous merge of activeSystemFieldFilters
    if (wrapperFnArr.length && result.filter?.relations) {
      result.filter.relations.version = (v) => {
        return wrapperFnArr.every((filter) => {
          if (typeof filter.filter?.relations?.version === 'function') {
            return filter.filter?.relations?.version(v)
          }

          throw Error('Expecting filter to be a function')
        })
      }
    }

    return result
  }, [activeFilters])

  const filterSelectorOpts: FilterAndSortOptions<DocumentORM> = useMemo(() => {
    const activeFilters = merge(
      docTenantFieldFilter,
      docSystemFieldFilter,
    )

    if (isPublisherSearch) {
      return activeFilters
    }

    return !unpublishedToggle
      // merge is also exported from the same query file as filters
      // we want to show active filters, the notPublished and has unpublished versions
      // the publisher is an special case because his 'published version includes the revoke, archive, and delete,
      ? (isPublisher || isDebugRoute)
        ? activeFilters
        // [NOTE] - Non publisher modes will ignore any active status filters (i.e. default active ones)
        // since merging will override any previous key/value
        : merge(activeFilters, filters.published)
      : merge(filters.hasUnpublishedVersion, activeFilters)
  }, [
    docTenantFieldFilter,
    docSystemFieldFilter,
    activeFilters,
    unpublishedToggle,
  ],
  );

  // [TODO]: Consider memoizing the functions?
  const value = {
    activeFilters,
    setActiveFilters,
    filterEntries,
    filterCategoryOrder,
    restrictToConfiguredFilterCategories,
    filterSelectorOpts,
    toggleFilter,
    resetToDefaultFilters,
    clearFilters,
    unpublishedToggle,
    toggleUnpublished: () => setUnpublishedToggle(p => !p),
    applyDefaultFiltersToInProgress,
  }

  return (
    <DNADocumentFilterContext.Provider value={value}>
      { children}
    </DNADocumentFilterContext.Provider>
  )
}

// ============================================================================
// HOC
// ============================================================================

// [TODO] - Correctly extract props from `Component` generic T for use in composition
export const withDNADocumentFilters = <T extends {
  initUserDefaultFilters?: boolean,
  applyDefaultFiltersToInProgress?: boolean,
}>
  (Component) => ({
    initUserDefaultFilters,
    applyDefaultFiltersToInProgress,
    ...restProps
  }: T) => {
    return (
      <DNADocumentFiltersProvider
        initUserDefaultFilters={initUserDefaultFilters}
        applyDefaultFiltersToInProgress={applyDefaultFiltersToInProgress}
      >
        <Component {...(restProps as T)} />
      </DNADocumentFiltersProvider>
    )
  }
